import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import Toaster from 'components/Toaster';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';
import DivisionModal from './DivisionModal';
import { useCreateDivision } from './gql/useCreateDivision';
import { useDeleteDivision } from './gql/useDeleteDivision';
import { useUpdateDivision } from './gql/useUpdateDivision';

const CREATE_ACCOUNT_DIVISION = gql`
  mutation CreateAccountDivision($input: CreateAccountDivisionInput!) {
    createAccountDivision(input: $input) {
      id
      name
      description
      image
      image_id
      video_url
    }
  }
`;

const UPDATE_ACCOUNT_DIVISION = gql`
  mutation UpdateAccountDivision($input: UpdateAccountDivisionInput!) {
    updateAccountDivision(input: $input) {
      id
      name
      description
      image
      image_id
      video_url
    }
  }
`;

const DELETE_ACCOUNT_DIVISION = gql`
  mutation DeleteAccountDivision($id: Int!) {
    deleteAccountDivision(id: $id)
  }
`;

// Section header with title and action button
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 4px 0 0 0;
`;

const HeaderLeft = styled.div``;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Divisions list
const DivisionsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DivisionCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) =>
      theme.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
    box-shadow: ${({ theme }) =>
      theme.name === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const DivisionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const DivisionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DivisionName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: ${({ isDeleted }) => (isDeleted ? 'line-through' : 'none')};
`;

const DivisionActions = styled.div`
  display: flex;
  gap: 8px;
`;

// Wrapper for action buttons (edit/delete)
const ActionButtonWrapper = styled.div`
  button {
    width: 36px;
    height: 36px;
    padding: 0;
  }
`;

// Empty state
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const FlagIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    <line x1="4" y1="22" x2="4" y2="15" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const DivisionsTab = ({ divisions = [], setFieldValue, accountId }) => {
  const [showDivisionModal, setShowDivisionModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toaster, setToaster] = useState({ isOpen: false });

  // Mutation hooks
  const { createDivision, loading: createLoading } = useCreateDivision();
  const { updateDivision, loading: updateLoading } = useUpdateDivision();
  const { deleteDivision } = useDeleteDivision();

  const isSubmitting = createLoading || updateLoading;

  const handleAddDivision = async (divisionData) => {
    // Check for duplicates
    const isDuplicate = divisions.some(
      (div) => div.name.toLowerCase() === divisionData.name.toLowerCase()
    );

    if (isDuplicate) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Duplicate Name',
        message: 'A division with this name already exists.',
      });
      return false;
    }

    const input = {
      account_id: parseInt(accountId),
      name: divisionData.name,
      description: divisionData.description || null,
      video_url: divisionData.video_url || null,
      image_id: divisionData.image_id || null,
    };

    const result = await createDivision(input);

    if (result.success) {
      // Add the new division to local state with server data
      const newDivision = {
        id: result.data.id,
        name: result.data.name,
        description: result.data.description,
        video_url: result.data.video_url,
        image: result.data.image,
        image_id: result.data.image_id,
      };
      setFieldValue('divisions', [...divisions, newDivision]);
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Division created successfully.',
      });
      return true;
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to create division.',
      });
      return false;
    }
  };

  const handleEditClick = (division) => {
    setSelectedDivision(division);
    setShowDivisionModal(true);
  };

  const handleAddClick = () => {
    setSelectedDivision(null);
    setShowDivisionModal(true);
  };

  const handleDivisionSubmit = async (divisionData) => {
    if (selectedDivision) {
      return handleEditDivision(divisionData);
    }
    return handleAddDivision(divisionData);
  };

  const handleEditDivision = async (divisionData) => {
    if (!selectedDivision) return false;

    // Check for duplicates (excluding current division)
    const isDuplicate = divisions.some(
      (div) =>
        div.id !== selectedDivision.id &&
        div.name.toLowerCase() === divisionData.name.toLowerCase()
    );

    if (isDuplicate) {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Duplicate Name',
        message: 'A division with this name already exists.',
      });
      return false;
    }

    const input = {
      id: parseInt(selectedDivision.id),
      name: divisionData.name,
      description: divisionData.description || null,
      video_url: divisionData.video_url || null,
      image_id: divisionData.image_id || null,
    };

    const result = await updateDivision(input);

    if (result.success) {
      // Update division in local state with server data
      const updatedDivisions = divisions.map((div) => {
        if (div.id === selectedDivision.id) {
          return {
            id: result.data.id,
            name: result.data.name,
            description: result.data.description,
            video_url: result.data.video_url,
            image: result.data.image,
            image_id: result.data.image_id,
          };
        }
        return div;
      });
      setFieldValue('divisions', updatedDivisions);
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Division updated successfully.',
      });
      return true;
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to update division.',
      });
      return false;
    }
  };

  const handleDeleteClick = (division) => {
    setSelectedDivision(division);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedDivision) return;

    const result = await deleteDivision(parseInt(selectedDivision.id));

    if (result.success) {
      // Remove division from local state
      const updatedDivisions = divisions.filter(
        (div) => div.id !== selectedDivision.id
      );
      setFieldValue('divisions', updatedDivisions);
      setToaster({
        isOpen: true,
        type: 'success',
        title: 'Success!',
        message: 'Division deleted successfully.',
      });
    } else {
      setToaster({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: result.error || 'Failed to delete division.',
      });
    }

    setShowDeleteModal(false);
    setSelectedDivision(null);
  };

  return (
    <>
      <SectionHeader>
        <HeaderLeft>
          <SectionTitle>
            Racing Divisions
            {divisions.length > 0 && (
              <ItemCount>({divisions.length})</ItemCount>
            )}
          </SectionTitle>
          <SectionSubtitle>
            Manage racing divisions for this account. These will be available
            for selection on all events.
          </SectionSubtitle>
        </HeaderLeft>
        <AddButtonWrapper>
          <ButtonV2
            elevated
            type="button"
            onClick={handleAddClick}
            icon={<PlusIcon />}
          >
            Add Division
          </ButtonV2>
        </AddButtonWrapper>
      </SectionHeader>

      {divisions.length === 0 ? (
        <Card>
          <EmptyState>
            <EmptyStateIcon>
              <FlagIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>No Divisions</EmptyStateTitle>
            <EmptyStateText>
              Create racing divisions to organize your events. Divisions help
              categorize participants and manage competition classes.
            </EmptyStateText>
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={handleAddClick}
                icon={<PlusIcon />}
              >
                Add Your First Division
              </ButtonV2>
            </AddButtonWrapper>
          </EmptyState>
        </Card>
      ) : (
        <DivisionsListContainer>
          {divisions.map((division) => (
            <DivisionCard key={division.id}>
              <DivisionIcon>
                <FlagIcon />
              </DivisionIcon>
              <DivisionInfo>
                <DivisionName>{division.name}</DivisionName>
              </DivisionInfo>
              <DivisionActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={() => handleEditClick(division)}
                    title="Edit division"
                    icon={<EditIcon />}
                  />
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    variant="icon"
                    type="button"
                    onClick={() => handleDeleteClick(division)}
                    title="Delete division"
                    icon={<TrashIcon />}
                  />
                </ActionButtonWrapper>
              </DivisionActions>
            </DivisionCard>
          ))}
        </DivisionsListContainer>
      )}

      <DivisionModal
        isOpen={showDivisionModal}
        onClose={() => {
          setShowDivisionModal(false);
          setSelectedDivision(null);
        }}
        onSubmit={handleDivisionSubmit}
        division={selectedDivision}
        loading={isSubmitting}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedDivision(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={selectedDivision?.name || ''}
        itemType="division"
        isDeleting={isDeleting}
      />

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </>
  );
};

DivisionsTab.propTypes = {
  divisions: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default DivisionsTab;
